/*
 * @providesModule MergeAccountScreen
 *
 */

import React, {useEffect, useState} from 'react';
import {Text, TouchableOpacity, View} from 'react-native';
import * as Animatable from 'react-native-animatable';
import {CurrencyView} from '@skillz/ui';
import SkillzImage from 'SkillzImage';
import SkillzButton from 'SkillzButton';
import {MergeAccountCard, UserCardInfo} from 'MergeAccountCard';
import styles from 'MergeAccountScreenStyle';
import PushButton from 'PushButton';
import SkillzStyle from 'SkillzStyle';
import {ScrollView} from 'react-native';
import SkillzSVG from 'SkillzSVG';
import {GameThemeBackground} from '../Components/GameThemeBackground';
import ClientPropertyConstants from 'ClientPropertyConstants';
import {_m} from 'INTL';

const TRANSLATION_DISTANCE = 73;

export interface MergeAccountScreenProps {
  footerText: string;
  bodyText: string;
  userAccounts: Array<UserCardInfo>;
  mergePress: (primaryAccount: UserCardInfo) => void;
  swapAccountsPress?: (newPrimaryAccount: UserCardInfo) => void;
  allowAccountSelection: boolean;
}

export const TEST_IDS = {
  CONTAINER: 'merge-account-screen-container',
  FOOTER_TEXT: 'merge-account-screen-footer-text',
  BODY_CONTAINER: 'merge-account-screen-body-container',
  BODY_TEXT: 'merge-account-screen-body-text',
  TOTAL_BALANCE: 'merge-account-screen-total-balance',
  TOTAL_BALANCE_TEXT: 'merge-account-screen-total-balance-text',
  IMAGE_CONTAINER: 'merge-account-screen-image-container',
  ACCOUNT_CARD: 'merge-account-screen-account-card',
  ACCOUNT_CARD_CONTAINER: 'merge-account-screen-account-card-container',
};

export default function MergeAccountScreen(props: MergeAccountScreenProps) {
  const [primaryAccount, setPrimaryAccount] = useState<UserCardInfo>();
  const {statusBarContentStyle} = SkillzStyle.fullStyles;
  const isDarkTheme = statusBarContentStyle === 0;

  useEffect(() => {
    if (props.userAccounts.length > 0) {
      setPrimaryAccount(props.userAccounts[0]);
    }
  }, [props.userAccounts]);

  //list of refs for each cell to be able to animate them with react-native-animatable
  const matchCellRef = props.userAccounts.reduce(
    (acc: Map<string, React.RefObject<Animatable.View>>, userAccount: UserCardInfo) => {
      acc.set(userAccount.userName, React.createRef<Animatable.View>());
      return acc;
    },
    new Map<string, React.RefObject<Animatable.View>>()
  );

  const userAccounts = props.userAccounts;

  let newAccountBalance = 0;
  //Sort for having the primary account first
  const accountCards = props.userAccounts.map((userAccount: UserCardInfo, index: number) => {
    newAccountBalance += userAccount.cashAmount;

    const onSelectFunc = props.allowAccountSelection
      ? (userAccount: UserCardInfo) => changePrimaryAccount(userAccount)
      : // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {};

    return (
      <Animatable.View ref={matchCellRef.get(userAccount.userName)} key={index}>
        <MergeAccountCard
          primary={userAccount === primaryAccount}
          userInfo={userAccount}
          onSelect={onSelectFunc}
          allowAccountSelection={props.allowAccountSelection}
          key={index}
          testID={TEST_IDS.ACCOUNT_CARD}
          isNewUI={ClientPropertyConstants.getFTUEV2FeatureEnabled()}
        />
      </Animatable.View>
    );
  });

  const animateCards = (userAccount: UserCardInfo) => {
    matchCellRef.get(userAccount.userName)?.current?.animate(slideUpAnim, 300, 0);
    if (primaryAccount) {
      matchCellRef.get(primaryAccount.userName)?.current?.animate(slideDownAnim, 300, 0);
    }
  };

  const slideUpAnim = {
    0: {
      translateY: 0,
      opacity: 1,
    },
    0.5: {
      translateY: -TRANSLATION_DISTANCE,
      opacity: 0,
    },
    0.51: {
      translateY: 0,
      opacity: 0,
    },
    1: {
      translateY: 0,
      opacity: 1,
    },
  };

  const slideDownAnim = {
    0: {
      translateY: 0,
      opacity: 1,
    },
    0.5: {
      translateY: TRANSLATION_DISTANCE,
      opacity: 0,
    },

    0.51: {
      translateY: 0,
      opacity: 0,
    },
    1: {
      translateY: 0,
      opacity: 1,
    },
  };

  const swapAccounts = (userAccount: UserCardInfo) => {
    if (!primaryAccount) {
      return;
    }

    const newPrimaryIndex = userAccounts.findIndex(
      (account: UserCardInfo) => account.userName === userAccount.userName
    );

    const oldPrimaryIndex = userAccounts.findIndex(
      (account: UserCardInfo) => account.userName === primaryAccount.userName
    );

    [userAccounts[oldPrimaryIndex], userAccounts[newPrimaryIndex]] = [
      userAccounts[newPrimaryIndex],
      userAccounts[oldPrimaryIndex],
    ];
  };

  const changePrimaryAccount = (userAccount: UserCardInfo) => {
    if (!primaryAccount || userAccount.userName === primaryAccount.userName) {
      return;
    }

    animateCards(userAccount);
    swapAccounts(userAccount);

    setTimeout(() => {
      setPrimaryAccount(userAccount);
    }, 100);

    props.swapAccountsPress?.(userAccount);
  };

  if (!primaryAccount) {
    return null;
  }

  if (ClientPropertyConstants.getFTUEV2FeatureEnabled()) {
    return (
      <>
        <GameThemeBackground />
        {/* <View style={styles.header}>
          <TouchableOpacity
            onPress={() => {
              NavigationManager.pop();
            }}
          >
            <SkillzSVG
              source={'arrow_left.svg'}
              style={{width: 24, height: 24}}
              resizeMode='contain'
            />
          </TouchableOpacity>
        </View> */}
        <ScrollView style={{flex: 1}} showsVerticalScrollIndicator={false}>
          <View style={styles.newContainer}>
            <View style={styles.imageContainer} testID={TEST_IDS.IMAGE_CONTAINER}>
              <SkillzSVG
                fill={SkillzStyle.getOverriddenFallbackTheme().textColorPrimary.color}
                source={isDarkTheme ? 'skillz_logo.svg' : 'skillz_logo_black.svg'}
                style={styles.newImage}
                resizeMode={'contain'}
                testID={TEST_IDS.IMAGE_CONTAINER}
              />
            </View>
            <View style={styles.bodyContainer} testID={TEST_IDS.BODY_CONTAINER}>
              <Text allowFontScaling={false} style={styles.newBodyText} testID={TEST_IDS.BODY_TEXT}>
                {props.bodyText}
              </Text>
              <View style={styles.accountCardContainer} testID={TEST_IDS.ACCOUNT_CARD_CONTAINER}>
                {accountCards}
              </View>
              <View style={styles.newTotalBalance} testID={TEST_IDS.TOTAL_BALANCE}>
                <CurrencyView
                  currencyCode='USD'
                  amount={newAccountBalance}
                  showIcon={true}
                  containerStyle={styles.newCurrencyViewContainer}
                  iconStyle={styles.newCurrencyIconStyles}
                  textStyle={styles.newCurrencyText}
                />
                <Text allowFontScaling={false} style={styles.newBalanceText}>
                  {_m('Your new account balance')}
                </Text>
              </View>
            </View>
            <View style={styles.newFooterContainer}>
              <Text
                allowFontScaling={false}
                style={styles.newFooterText}
                testID={TEST_IDS.FOOTER_TEXT}
              >
                {props.footerText}
              </Text>

              <PushButton
                title={_m('Merge accounts')}
                style={styles.newButton}
                colors={SkillzStyle.getOverriddenFallbackTheme().playButtonGradient}
                textStyle={{
                  color: SkillzStyle.getOverriddenFallbackTheme().textColorPrimary.color,
                  fontWeight: '700',
                }}
                cornerRadius={12}
                onPress={() => props.mergePress(primaryAccount)}
              />
            </View>
          </View>
        </ScrollView>
      </>
    );
  }

  return (
    <View style={styles.container} testID={TEST_IDS.CONTAINER}>
      <View style={styles.imageContainer} testID={TEST_IDS.IMAGE_CONTAINER}>
        <SkillzImage
          source={'skillz_powered_logo.svg'}
          style={styles.image}
          resizeMode={'contain'}
        />
      </View>
      <View style={styles.bodyContainer} testID={TEST_IDS.BODY_CONTAINER}>
        <View>
          <Text allowFontScaling={false} style={styles.bodyText} testID={TEST_IDS.BODY_TEXT}>
            {props.bodyText}
          </Text>
        </View>
        <View style={styles.accountCardContainer} testID={TEST_IDS.ACCOUNT_CARD_CONTAINER}>
          {accountCards}
        </View>
        <View style={styles.totalBalance} testID={TEST_IDS.TOTAL_BALANCE}>
          <Text allowFontScaling={false} style={styles.balanceText}>
            {'Your New Account Balance:'}
          </Text>
          <CurrencyView
            currencyCode='USD'
            amount={newAccountBalance}
            showIcon={true}
            containerStyle={styles.currencyViewContainer}
            iconStyle={styles.currencyIconStyles}
            textStyle={styles.currencyText}
          />
        </View>
      </View>
      <View style={styles.footerContainer}>
        <Text allowFontScaling={false} style={styles.footerText} testID={TEST_IDS.FOOTER_TEXT}>
          {props.footerText}
        </Text>

        <SkillzButton
          title='Merge'
          containerStyle={styles.button}
          type={'solid'}
          onPress={() => props.mergePress(primaryAccount)}
        />
      </View>
    </View>
  );
}
